import { classNames } from "Utils/utils";
import MultiSelectDropdown from "components/common/MultiSelectDropdown";
import AsyncMultiSelectDropdown from "components/common/AsyncMultiSelectDropdown";
import SelectOption from "components/common/Select/Select";
import { OUTCOME_OPTIONS } from "../../constants";
import useCareLabels from "hooks/useCareLabels";

export const BasicDetailsStep = ({
  participantOptions,
  assignedParticipantsLoading,
  workerOptions,
  showWorkerField = false,
  workersRequired = false,
  workersDisabled = false,
  timesheetOptions = [],
  timesheetLoading = false,
  selectedTimesheet = null,
  onTimesheetChange,
  timesheetLocked = false,
  timesheetDisabled = false,
  showTimesheetPicker = true,
  control,
  setValue,
  watch,
  clearErrors,
  selectedOutcome,
  setSelectedOutcome,
  participantLabel: participantLabelProp,
  participantLabelPlural: participantLabelPluralProp,
  participantSearchOptions,
  participantSearchLoading,
  hasMoreParticipants,
  onParticipantInputChange,
  onParticipantMenuScrollToBottom,
  onParticipantMenuOpen,
}) => {
  const {
    participantLabel: participantLabelHook,
    participantLabelPlural: participantLabelPluralHook,
  } = useCareLabels();
  const participantLabel = participantLabelProp ?? participantLabelHook;
  const participantLabelPlural = participantLabelPluralProp ?? participantLabelPluralHook;
  const timesheetOff = timesheetLocked || timesheetDisabled;
  const participantSpan = showWorkerField ? "" : "lg:col-span-2";

  return (
    <div className="space-y-8 animate-fadeIn">
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          People Involved
        </h3>
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <div className={participantSpan}>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {participantLabel} Name (Multi-select) <span className="text-red-500">*</span>
            </label>
            <AsyncMultiSelectDropdown
              showIcon
              options={participantSearchOptions}
              isLoading={participantSearchLoading}
              hasMore={hasMoreParticipants}
              onInputChange={onParticipantInputChange}
              onMenuScrollToBottom={onParticipantMenuScrollToBottom}
              onMenuOpen={onParticipantMenuOpen}
              name="selectedParticipants"
              control={control}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              isDisabled={false}
              loadingPlaceholder={`Loading ${participantLabelPlural.toLowerCase()}...`}
              noOptionsPlaceholder={`No ${participantLabelPlural.toLowerCase()} available`}
              noOptionsSearchPlaceholder={(q) => `No ${participantLabelPlural.toLowerCase()} found for "${q}"`}
            />
            {showTimesheetPicker && !timesheetLocked && (
              <p className="mt-1 text-xs text-gray-500">
                Filled from the timesheet. You can add or remove people.
              </p>
            )}
          </div>
          {showWorkerField && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Worker Name{" "}
                <span className="text-gray-500 font-normal text-xs">
                  (Optional)
                </span>
              </label>
              <MultiSelectDropdown
                showIcon
                data={workerOptions}
                name="selectedWorkers"
                control={control}
                setValue={setValue}
                watch={watch}
                clearErrors={clearErrors}
                isDisabled={assignedParticipantsLoading}
              />
            </div>
          )}
          {showTimesheetPicker && (
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Timesheet <span className="text-red-500">*</span>
              </label>
              <SelectOption
                isSearch
                data={timesheetOptions}
                selectedOption={selectedTimesheet}
                handleChange={onTimesheetChange}
                placeHolder={
                  timesheetLoading ? "Loading timesheets..." : "Select a timesheet"
                }
                isDisabled={timesheetOff || timesheetLoading}
                isClearable={!timesheetOff}
              />
              {timesheetLocked && (
                <p className="mt-1 text-xs text-gray-500">
                  Linked to this shift.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Incident Outcome <span className="text-red-500">*</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {OUTCOME_OPTIONS.map((outcome) => (
            <label
              key={outcome.value}
              className={classNames(
                "flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                selectedOutcome?.value === outcome.value
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              )}
            >
              <input
                type="radio"
                checked={selectedOutcome?.value === outcome.value}
                onChange={() => setSelectedOutcome(outcome)}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div>
                <span className="font-medium text-gray-900">
                  {outcome.label}
                </span>
                <p className="text-xs text-gray-500 mt-0.5">
                  {outcome.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
};
